import { createFileRoute, Link } from "@tanstack/react-router";
import { Facebook, Instagram, Mail } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Section,
} from "@alight/ui";
import { useSeo } from "../lib/seo";
import { useBlock } from "../lib/content";
import { PageIntro } from "../components/PageIntro";
import { JsonLd } from "../components/JsonLd";
import { faqJsonLd } from "../lib/structured-data";

export const Route = createFileRoute("/faq")({
  component: FaqPage,
});

function FaqPage() {
  const faq = useBlock("faq");
  const contact = useBlock("contact");
  const banners = useBlock("banners");

  useSeo({
    title: "FAQ | Connect Sydney",
    description:
      "Who Connect is for, what to bring, how to get there in Sydney, what's the deal with dinner, and what if you're not going to uni — answered.",
    path: "/faq",
    image: banners.faqImage,
  });

  return (
    <>
      <JsonLd data={faqJsonLd(faq)} />
      <PageIntro
        eyebrow="Good to know"
        title="Frequently asked questions"
        description={faq.intro}
        image={banners.faqImage}
        imageAlt={banners.faqImageAlt}
      />

      <Section tone="paper" width="narrow" spacing="lg">
        <Accordion type="single" collapsible className="divide-y divide-border rounded-card border border-border bg-card px-6">
          {faq.items.map((item, i) => (
            <AccordionItem key={i} value={`q-${i}`} className="border-0">
              <AccordionTrigger className="py-5 text-left font-heading text-lg font-bold text-ink hover:no-underline">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-base leading-relaxed text-muted">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      <Section tone="card" width="narrow" spacing="lg">
        <div className="text-center">
          <h2 className="font-heading text-3xl font-extrabold text-ink">{faq.moreTitle}</h2>
          <p className="mt-4 text-muted">{faq.moreBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild variant="accent">
              <Link to="/contact">
                <Mail className="mr-1.5 h-4 w-4" aria-hidden />
                Send a message
              </Link>
            </Button>
            <Button asChild variant="outline">
              <a href={contact.instagramUrl} target="_blank" rel="noreferrer">
                <Instagram className="mr-1.5 h-4 w-4" aria-hidden />
                Instagram
              </a>
            </Button>
            <Button asChild variant="outline">
              <a href={contact.facebookUrl} target="_blank" rel="noreferrer">
                <Facebook className="mr-1.5 h-4 w-4" aria-hidden />
                Facebook
              </a>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
